// ============================================================================
// LIT-CASE-001 — Litigation Cases (Master List) — Phase 9a
// ============================================================================
// Read-only list of all non-PRE_LITIGATION cases for the user's scope.
// Row click opens the LitigationCaseDetailModal (LIT-CASE-002) via the
// openLitigationCase() bridge. Phase 9b/c wire claim / log_action / etc.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import StatusPill from 'components/common/StatusPill';
import { useLitigationCases } from 'hooks/api/useLitigationCases';
import { useCaseTypeOptions } from 'hooks/api/useCaseTypes';
import type {
  LitigationCaseListItem,
  LitigationCaseSearchRequest,
  LitigationCaseTypeCode,
} from 'api/types/litigationCase.types';
import { openLitigationCase } from 'components/modules/litigation-module/openLitigationCase';

interface LitigationCasesScreenProps {
  tabId?: string;
  moduleName?: string;
  /** Optional category narrowing. RECOVERY = Repo/Auction/SARFAESI;
   *  LITIGATION = Arbitration/NI-138/Civil/EP. Omit for all non-pre-lit. */
  category?: 'RECOVERY' | 'LITIGATION';
  /** Override the screen title (defaults derived from category). */
  title?: string;
  /** Override the screen description. */
  description?: string;
}

const POOL_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '',            label: 'Open only (default)' },
  { value: 'UNCLAIMED',   label: 'Unclaimed only' },
  { value: 'IN_PROGRESS', label: 'In Progress only' },
  { value: 'CLOSED',      label: 'Closed only' },
  { value: 'ALL',         label: 'All' },
];

const LitigationCasesScreen: React.FC<LitigationCasesScreenProps> = ({ tabId, category, title, description }) => {
  const [queryParams, setQueryParams] = useState<LitigationCaseSearchRequest>(
    { page: 1, pageSize: 25, category });
  const { data, loading, pagination, refetch } = useLitigationCases(queryParams);

  // Re-sync category if the prop changes (e.g. tab switch in some setups).
  useEffect(() => {
    setQueryParams(prev => prev.category === category ? prev : { ...prev, category, page: 1 });
  }, [category]);

  // Case-type filter sourced from mst.tbl_case_type — exclude PRE_LITIGATION,
  // and (when category prop is set) the chips only need the matching subset.
  // For now we keep all non-pre-lit types in the dropdown; the API will only
  // return the matching ones thanks to the category filter.
  const { options: allCaseTypeOptions } = useCaseTypeOptions();
  const CASE_TYPE_OPTIONS = useMemo<{ value: string; label: string }[]>(
    () => [
      { value: '', label: 'All types' },
      ...allCaseTypeOptions.filter(o => o.value !== 'PRE_LITIGATION'),
    ],
    [allCaseTypeOptions],
  );

  // Defaults derived from category if title/description weren't passed.
  const screenTitle = title ??
    (category === 'RECOVERY'  ? 'Recovery Cases'
   : category === 'LITIGATION' ? 'Litigation Cases'
   : 'Cases');
  const screenDescription = description ??
    (category === 'RECOVERY'
      ? 'Operational recovery — vehicle repossession, auction, SARFAESI. Click a row to open the case detail.'
   : category === 'LITIGATION'
      ? 'Court matters — arbitration, NI-Act §138, civil suit, execution. Click a row to open the case detail.'
   : 'Cases under arbitration, NI-Act §138, civil suit, SARFAESI, or execution. Click a row to open the case detail.');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [caseTypeFilter,  setCaseTypeFilter]  = useState<string>('');
  const [poolStatusFilter, setPoolStatusFilter] = useState<string>('');

  // Server-side search debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback(
    (pageIndex: number) => setQueryParams(prev => ({ ...prev, page: pageIndex + 1 })),
    []);
  const handlePageSizeChange = useCallback(
    (pageSize: number) => setQueryParams(prev => ({ ...prev, pageSize, page: 1 })),
    []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      caseTypeCode: (caseTypeFilter as LitigationCaseTypeCode) || undefined,
      caseStatus: poolStatusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [caseTypeFilter, poolStatusFilter]);

  const handleClearFilters = useCallback(() => {
    setCaseTypeFilter('');
    setPoolStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.caseTypeCode) n++;
    if (queryParams.caseStatus)   n++;
    return n;
  }, [queryParams.caseTypeCode, queryParams.caseStatus]);

  const handleOpen = useCallback((row: LitigationCaseListItem) => {
    openLitigationCase(String(row.id));
  }, []);

  const columns: SyutGridColumn<LitigationCaseListItem>[] = useMemo(() => [
    { id: 'caseCode',         header: 'Case #',     accessorKey: 'caseCode',         size: 180, enableSorting: true,
      cell: ({ row }) => <strong>{row.original.caseCode}</strong>,
    },
    { id: 'caseTypeCode',     header: 'Type',       accessorKey: 'caseTypeCode',     size: 130, enableSorting: true,
      cell: ({ row }) => row.original.caseTypeDisplayName ?? row.original.caseTypeCode },
    { id: 'accountNumber',    header: 'Account #',  accessorKey: 'accountNumber',    size: 140, enableSorting: true },
    { id: 'customerName',     header: 'Customer',   accessorKey: 'customerName',     size: 220, enableSorting: true },
    { id: 'currentStateCode', header: 'State',      accessorKey: 'currentStateCode', size: 180, enableSorting: true,
      cell: ({ row }) => row.original.currentStateDisplayName ?? row.original.currentStateCode ?? <span style={{ color: 'var(--text-secondary)' }}>—</span> },
    { id: 'caseStatus',       header: 'Status',     accessorKey: 'caseStatus',       size: 130, enableSorting: true,
      cell: ({ row }) => {
        const ps = row.original.caseStatus;
        if (ps === 'UNCLAIMED')   return <StatusPill label="Unclaimed"   tone="warning" size="xs" />;
        if (ps === 'IN_PROGRESS') return <StatusPill label="In Progress" tone="info"    size="xs" />;
        if (ps === 'ON_HOLD')     return <StatusPill label="On Hold"     tone="warning" size="xs" />;
        if (ps === 'CLOSED')      return <StatusPill label="Closed"      tone="neutral" size="xs" />;
        return ps;
      } },
    { id: 'assignedAgentName', header: 'Agent',     accessorKey: 'assignedAgentName', size: 160, enableSorting: true,
      cell: ({ row }) => row.original.assignedAgentName ?? <span style={{ color: 'var(--text-secondary)' }}>—</span> },
    { id: 'currentDpd',       header: 'DPD (now)',  accessorKey: 'currentDpd',       size: 100, enableSorting: true },
    { id: 'currentOutstanding', header: 'Outstanding', accessorKey: 'currentOutstanding', size: 140, enableSorting: true,
      cell: ({ row }) => `₹${(row.original.currentOutstanding ?? 0).toLocaleString('en-IN')}` },
    { id: 'openedAt',         header: 'Opened',     accessorKey: 'openedAt',         size: 130, enableSorting: true,
      cell: ({ row }) => row.original.openedAt ? new Date(row.original.openedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
  ], []);

  const getRowActions = useCallback((row: LitigationCaseListItem): ActionMenuItem[] => [
    {
      id: 'open',
      label: 'Open Case',
      icon: <IconSystem.eye size={16} />,
      onClick: () => handleOpen(row),
    },
  ], [handleOpen]);

  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: () => refetch(),
      variant: 'icon',
    },
  ], [refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title={screenTitle}
        description={screenDescription}
        icon={<IconSystem.tag size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search case #, account, customer...',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              frozenRightColumns={['actions']}
              isLoading={loading}
              emptyMessage="No litigation cases found"
            />
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Case Type"
                options={CASE_TYPE_OPTIONS}
                value={caseTypeFilter}
                onChange={(v) => setCaseTypeFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Pool Status"
                options={POOL_STATUS_OPTIONS}
                value={poolStatusFilter}
                onChange={(v) => setPoolStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default LitigationCasesScreen;
